"use client";
import { useState } from "react";

import Button from "@stadium/shared/src/components/shared/button";
import RangeTextInput from "@stadium/ui/src/components/form/RangeTextInput";
import { Spinner } from "@stadium/ui/src/components/loaders/loaders";

import { getProductRangeInputFormErrors } from "@stadium/helpers/src/product/utils";
import { normalizeCount } from "@stadium/helpers/form/formatter";

import Modal from "@stadium/ui/src/components/ModalNew";
import {
  CloseButton,
  ModalBody,
  ModalContent,
  ModalDialog,
  ModalHeader,
  ModalFooter,
} from "@stadium/ui/src/components/ModalNew/utils";
import { DEFAULT_CURRENCY_CODE } from "@stadium/constants";

import styles from "./main.module.scss";

const defaultObj: DefaultObjType = {};

const EditPriceRangeModal = ({ closeModal, ...data }) => {
  const {
    onSave,
    priceRange,
    setPriceRange,
    defaultPrices,
    totalProductsCount,
    loadingProductCount,
    headingInfo = "",
    isSaveCtaLoading = false,
  } = data;
  const [formErrors, setFormErrors] = useState(defaultObj);

  const { minPrice: minPriceOfPricelessShop, maxPrice: maxPriceOfPricelessShop } = priceRange;

  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const resetPriceRange = () => {
    closeModal();
    setPriceRange({
      minPrice: defaultPrices.minPrice,
      maxPrice: defaultPrices.maxPrice,
    });
  };

  const validateAndSubmit = () => {
    if (
      +minPriceOfPricelessShop === defaultPrices.minPrice &&
      +maxPriceOfPricelessShop === defaultPrices.maxPrice
    ) {
      resetPriceRange();
    }
    const errors = getProductRangeInputFormErrors({
      minPrice: minPriceOfPricelessShop,
      maxPrice: maxPriceOfPricelessShop,
    });
    if (Object.keys(errors).length === 0) {
      onSave({ minPriceVal: minPriceOfPricelessShop, maxPriceVal: maxPriceOfPricelessShop });
    }
    setFormErrors(errors);
  };

  const handlePriceRangeChange = (type, { value }) => {
    const fieldChanged = `${type}Price`;
    const newPriceRange = { ...priceRange, [fieldChanged]: value };
    setPriceRange(newPriceRange);
    if (newPriceRange.minPrice && newPriceRange.maxPrice && +newPriceRange.maxPrice) {
      return setIsButtonDisabled(false);
    }
    return setIsButtonDisabled(true);
  };

  return (
    <Modal className={styles.priceRangeModal} mobileBottomDrawer closeModal={closeModal}>
      <ModalDialog className={styles.modalDialog}>
        <ModalContent className={`${styles.modalMobileBottomContent} ${styles.modalContent}`}>
          <ModalHeader className={`${styles.modalHeader} ${styles.headerBar}`}>
            <div>
              <h1 className={styles.modalHeading}>Edit Price Range</h1>
              <p className={styles.headingInfo}>{headingInfo}</p>
            </div>
            <CloseButton className={styles.closeIcon} onClick={closeModal} />
          </ModalHeader>

          <ModalBody className={`${styles.modalBody} ${styles.bodyBlock}`}>
            <div className={styles.labelInfo}>Price Range</div>
            <div className={styles.priceRangeSection}>
              <RangeTextInput
                minValue={{
                  value: minPriceOfPricelessShop,
                  error: formErrors?.minPrice,
                }}
                maxValue={{
                  value: maxPriceOfPricelessShop,
                  error: formErrors?.maxPrice,
                }}
                onChange={handlePriceRangeChange}
                validator={(e) => ({
                  ...e,
                  value: normalizeCount(e.value, { fraction: true }),
                })}
                suffix={<span className={styles.placeHolderText}>{DEFAULT_CURRENCY_CODE}</span>}
                rowClass={styles.rangeFormRow}
                inputClass={styles.textInput}
              />
              <div className={styles.productLoadSection}>
                <span className={styles.spanText}>=</span>
                {loadingProductCount ? (
                  <Spinner className={styles.sectionLoader} />
                ) : (
                  totalProductsCount
                )}{" "}
                products in range in all countries
              </div>
            </div>
          </ModalBody>

          <ModalFooter className={`${styles.modalFooter} ${styles.footerBlock}`}>
            <Button
              onClick={resetPriceRange}
              styleType="primary-outline"
              className={styles.cancelBtn}
            >
              Cancel
            </Button>
            <Button
              onClick={validateAndSubmit}
              disabled={isButtonDisabled}
              styleType="primary"
              className={styles.saveCta}
              isLoading={isSaveCtaLoading}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </ModalDialog>
    </Modal>
  );
};

export default EditPriceRangeModal;
